import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChefHat, PackageCheck, ClipboardCheck, CheckSquare, ShieldCheck,
  AlertTriangle, Check, X, Lock, ArrowRight, Truck, Trash2, Sprout, Flame, Package, TrendingUp,
  ClipboardList,
} from "lucide-react";
import api from "../lib/api";
import { Card, Badge, PageHeader, PageLoader } from "../lib/ui";
import { fmtNum } from "../lib/constants";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const STEP_META = {
  receiving: { icon: PackageCheck, perm: "view_receiving" },
  preparation: { icon: ChefHat, perm: "view_prepare" },
  waste: { icon: Trash2, perm: "view_waste" },
  stock_take: { icon: ClipboardCheck, perm: "view_stock_take" },
  order: { icon: ClipboardList, perm: "view_orders" },
  closing: { icon: CheckSquare, perm: "view_orders" },
};

const STATUS_TONE = { done: "success", active: "primary", locked: "mute" };

function Kpi({ icon: Icon, label, value, sub, tone = "text-primary" }) {
  return (
    <Card className="p-4" data-testid={`bk-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-ink-mute">
        <Icon size={14} className={tone} /> <span className="truncate">{label}</span>
      </div>
      <p className="mt-1 font-heading text-2xl font-extrabold text-ink">{value}</p>
      {sub != null && <p className="text-[11px] text-ink-mute">{sub}</p>}
    </Card>
  );
}

function SplitCard({ icon: Icon, label, data, tone, onClick }) {
  return (
    <button onClick={onClick} data-testid={`bk-split-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className="flex items-center gap-3 rounded-2xl border border-line bg-white p-4 text-left transition hover:-translate-y-0.5 hover:shadow-card">
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${tone}`}><Icon size={22} /></span>
      <div className="min-w-0 flex-1">
        <p className="font-heading text-base font-extrabold text-ink">{fmtNum(data.count)}</p>
        <p className="truncate text-xs text-ink-mute">{label}</p>
      </div>
      <div className="flex flex-col items-end gap-1">
        {data.critical > 0 && <Badge tone="danger">{data.critical}</Badge>}
        {data.low > 0 && <Badge tone="warn">{data.low}</Badge>}
      </div>
    </button>
  );
}

export default function BranchKitchen() {
  const { t } = useT();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/workspace/branch-kitchen"); setData(data); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <PageLoader />;
  const { readiness: r, kpis: k, inventory_split: inv } = data;
  const showYield = hasPermission("view_prepare_dashboard");
  const isBeverage = user?.station === "beverage";

  return (
    <div className="animate-fade-up space-y-5 pb-24 lg:pb-2" data-testid="branch-kitchen">
      <PageHeader icon={ChefHat}
        title={isBeverage ? t("Beverage Station Operations") : t("Branch Kitchen Operations")}
        subtitle={`${data.branch_name} · ${isBeverage ? t("Daily beverage command center") : t("Daily kitchen command center")}`} />

      {/* ---- Kitchen Readiness hero ---- */}
      <Card className={`relative overflow-hidden p-0 ${r.ready ? "ring-1 ring-success/30" : "ring-1 ring-warn/30"}`} data-testid="bk-readiness">
        <div className={`flex flex-col gap-4 p-5 sm:flex-row sm:items-center ${r.ready ? "bg-success-light/40" : "bg-warn-light/40"}`}>
          <div className="flex items-center gap-4">
            <span className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl ${r.ready ? "bg-success text-white" : "bg-warn text-white"}`}>
              {r.ready ? <ShieldCheck size={30} /> : <AlertTriangle size={30} />}
            </span>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-ink-mute">{t("Kitchen Readiness")}</p>
              <p className={`font-heading text-3xl font-extrabold ${r.ready ? "text-success-text" : "text-warn-text"}`} data-testid="bk-readiness-status">
                {r.ready ? t("READY") : t("NOT READY")}
              </p>
              <p className="text-xs text-ink-soft">{r.ready ? t("Kitchen is ready to operate") : t("Complete all checks to open the kitchen")} · {r.completed}/{r.total}</p>
            </div>
          </div>
          <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
            {r.checks.map((c) => (
              <div key={c.key} className="flex items-center gap-2 rounded-xl bg-white/70 px-3 py-2" data-testid={`bk-check-${c.key}`}>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${c.done ? "bg-success text-white" : "bg-stone-200 text-ink-mute"}`}>
                  {c.done ? <Check size={13} /> : <X size={13} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-ink">{t(c.label)}</p>
                </div>
                <span className="shrink-0 text-[11px] text-ink-mute">{c.detail}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* ---- KPI strip ---- */}
      <div className={`grid grid-cols-2 gap-3 ${showYield ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
        <Kpi icon={Truck} label={t("Today's Deliveries")} value={k.deliveries_pending} sub={`${k.received_today} ${t("received")}`} tone="text-indigo-600" />
        <Kpi icon={Sprout} label={t("Raw Inventory")} value={k.raw_count} sub={`${k.raw_low} ${t("low")}`} tone="text-amber-700" />
        <Kpi icon={Flame} label={t("RTC Inventory")} value={k.rtc_count} sub={k.rtc_critical > 0 ? `${k.rtc_critical} ${t("critical")}` : t("healthy")} tone="text-orange-600" />
        <Kpi icon={Trash2} label={t("Today's Waste")} value={fmtNum(k.waste_qty)} sub={`${k.waste_count} ${t("records")}`} tone="text-rose-600" />
        {showYield && <Kpi icon={TrendingUp} label={t("Yield Performance")} value={k.avg_yield != null ? `${k.avg_yield}%` : "—"} sub={t("today")} tone="text-emerald-600" />}
      </div>

      {/* ---- Guided 6-step workflow ---- */}
      <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink-mute">{t("Daily Workflow")}</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.steps.map((s, idx) => {
            const meta = STEP_META[s.key] || STEP_META.closing;
            const allowed = hasPermission(meta.perm);
            const Icon = meta.icon;
            const clickable = allowed;
            const tone = !allowed ? "mute" : STATUS_TONE[s.status];
            return (
              <button key={s.key} disabled={!clickable}
                onClick={() => clickable && navigate(s.route)} data-testid={`bk-step-${s.key}`}
                className={`group flex items-center gap-3 rounded-2xl border p-4 text-left transition ${
                  !allowed ? "cursor-not-allowed border-line bg-stone-50 opacity-60" :
                  s.status === "active" ? "border-primary bg-primary-light/40 hover:shadow-card" :
                  s.status === "done" ? "border-success/30 bg-success-light/30 hover:shadow-card" :
                  "border-line bg-white hover:shadow-card"}`}>
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                  s.status === "done" ? "bg-success text-white" :
                  s.status === "active" ? "bg-primary text-white" : "bg-stone-100 text-ink-mute"}`}>
                  {s.status === "done" ? <Check size={22} /> : <Icon size={20} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold text-ink-mute">{idx + 1}</span>
                    <p className="truncate font-heading text-sm font-extrabold text-ink">{t(s.label)}</p>
                  </div>
                  <p className="truncate text-xs text-ink-mute">{s.detail}</p>
                </div>
                {!allowed ? <Lock size={15} className="text-ink-mute" /> :
                  <Badge tone={tone}>{t(s.status === "done" ? "Done" : s.status === "active" ? "Active" : "Locked")}</Badge>}
              </button>
            );
          })}
        </div>
      </div>

      {/* ---- Inventory split (Raw / RTC / Finished) ---- */}
      <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink-mute">{t("Inventory Split")}</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <SplitCard icon={Sprout} label={t("Raw Inventory")} data={inv.raw} tone="bg-amber-50 text-amber-700" onClick={() => navigate("/my-inventory")} />
          <SplitCard icon={Flame} label={t("Ready-to-Cook Inventory")} data={inv.rtc} tone="bg-orange-50 text-orange-600" onClick={() => navigate("/my-inventory")} />
          <SplitCard icon={Package} label={t("Finished Products")} data={inv.finished} tone="bg-emerald-50 text-emerald-600" onClick={() => navigate("/my-inventory")} />
        </div>
      </div>

      <Card className="flex items-center gap-4 p-5" data-testid="bk-handover">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-light text-primary"><ChefHat size={24} /></span>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-base font-extrabold text-ink">{t("One standard workflow, every day")}</p>
          <p className="text-xs text-ink-mute">{t("Receiving → Preparation → Waste → Stock Take → Tomorrow's Branch Order → Close Kitchen")}</p>
        </div>
        <ArrowRight size={18} className="text-primary" />
      </Card>
    </div>
  );
}
